<template>
  <card>
    <template #header>
      <h2 class="font-medium text-blue dark:text-white">
        Diversity in faculty
      </h2>
    </template>

    <template #body>
      <div class="text-center">
        {{ school.latest_faculty.fulltime }} full-time faculty
        <term name="fullTimeFaculty"/>
        in {{ school.latest_faculty.academic_year }}-{{ school.latest_faculty.academic_year - 1999 }} AY
      </div>
      <div class="flex justify-around items-center rounded-b-lg">
        <div class="text-center">
          <div id="race"/>
          <div class="text-center">
            <div class="text-blue dark:text-white text-3xl font-semibold">
              {{ pocPercent }}%
            </div>
            <div class="text-gray-500 text-sm">
              faculty of color
            </div>
          </div>
        </div>
        <div class="text-center">
          <div id="gender"/>
          <div class="text-center">
            <div class="text-blue dark:text-white text-3xl font-semibold">
              {{ womenPercent }}%
            </div>
            <div class="text-gray-500 text-sm">
              women
            </div>
          </div>
        </div>
      </div>
      <div class="text-sm mt-3">
        Other faculty
        <term name="otherFaculty"/>
        was comprised of
        {{ otherWomenPercent }}%
        women, and
        {{ otherPocPercent }}%
        faculty of color.
      </div>
    </template>
  </card>
</template>

<script setup>
import Card from "@/components/cards/Card.vue"
import Highcharts from "@/highcharts"
import Term from "@/components/Term.vue"
import colors from "@/colors"
import { computed, onMounted } from "vue"
import { percent } from "@/support"

const props = defineProps({
  school: { type: Object, required: true },
  id: { type: String, required: true },
})

const chartOptions = {
  chart: {
    type: "pie",
    margin: [0, 0, 0, 0],
    spacing: [0, 0, 0, 0],
    width: 130,
    height: 130,
  },
  title: {
    text: "",
  },
  tooltip: {
    formatter() {
      return `<b>${this.point.name}</b> ${this.y}%`
    },
  },
  plotOptions: {
    pie: {
      shadow: false,
      allowPointSelect: false,
      cursor: "pointer",
      dataLabels: {
        enabled: false,
      },
    },
  },
}
const series = {
  type: "pie",
  animation: false,
  groupPadding: 0,
  pointPadding: 0,
}

const poc = computed(() => props.school.latest_faculty.poc_FT)

const pocPercent = computed(() => percent(poc.value, props.school.latest_faculty.fulltime))
const womenPercent = computed(() => percent(props.school.latest_faculty.women_FT, props.school.latest_faculty.fulltime))
const otherPocPercent = computed(() => percent(props.school.latest_faculty.poc_nonFT, props.school.latest_faculty.poc_nonFT + props.school.latest_faculty.allfaculty - props.school.latest_faculty.poc_nonFT))
const otherWomenPercent = computed(() => percent(props.school.latest_faculty.women_nonFT, props.school.latest_faculty.nonfulltime))

onMounted(() => {
  const faculty = props.school.latest_faculty

  const raceSeries = {
    ...series,
    name: "race",
    data: [
      { y: pocPercent.value, name: "Of color", color: colors.blue[400] },
      { y: percent(faculty.fulltime - poc.value, faculty.fulltime), name: "White", color: colors.orange[400] },
    ],
  }

  const genderSeries = {
    ...series,
    name: "gender",
    data: [
      { y: percent(faculty.men_FT, faculty.fulltime), name: "Men", color: colors.indigo[400] },
      { y: percent(faculty.women_FT, faculty.fulltime), name: "Women", color: colors.yellow[400] },
      { y: percent(faculty.AGI_FT, faculty.fulltime), name: "Another gender", color: colors.rose[400] },
      { y: percent(faculty.PNR_FT, faculty.fulltime), name: "Not reported", color: colors.teal[400] },
    ],
  }

  new Highcharts.Chart("race", {
    ...chartOptions, series: [raceSeries],
  })
  new Highcharts.Chart("gender", {
    ...chartOptions, series: [genderSeries],
  })
})
</script>
